import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ScanText, Loader2, AlertTriangle, Calendar, DollarSign, Sparkles,
  ShieldAlert, FileSearch, Quote, ShieldCheck, Flag, X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Public, unauthenticated page for a renter's shared deep-read analysis.
 *
 * Two non-negotiable visual elements at the very top:
 *   1. Strong platform-not-publisher disclaimer that the renter is the
 *      author of this share and TenantTrail does not verify or endorse it.
 *   2. Standard "general information, not legal advice" closing.
 *
 * Includes a takedown report form. Two reports auto-revoke the share.
 */
export default function SharedFinding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/findings-share/${token}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "This share is no longer available.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-[#0D2B52] font-serif">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading the report...
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center px-6 text-center" data-testid="shared-finding-error">
        <FileSearch size={28} strokeWidth={1.5} className="text-[#6B7280] mb-3" />
        <h1 className="text-2xl text-[#0D2B52] font-serif mb-2">Report unavailable</h1>
        <p className="text-sm text-[#6B7280] max-w-md mb-6 font-serif">{error}</p>
        <Link to="/" className="btn-primary text-sm">Visit TenantTrail</Link>
      </div>
    );
  }

  const a = data?.analysis || {};
  const flagCount = (a.red_flags?.length || 0) + (a.clauses_to_watch?.length || 0);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A]" data-testid="shared-finding-page">
      {/* Header / branding */}
      <header className="bg-white border-b border-[#E5E4DE] px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#0D2B52] font-medium">
            <FileSearch size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
            TenantTrail
          </Link>
          <Link to="/" className="text-xs text-[#1B6C62] hover:underline font-mono uppercase tracking-wider">Get your own</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Platform-not-publisher disclaimer, prominently at the top */}
        <div className="bg-[#FAF6E8] border-l-4 border-[#C9A961] p-4 space-y-2" data-testid="share-platform-disclaimer">
          <div className="label-mono text-[10px] text-[#8A6D3B] flex items-center gap-1">
            <ShieldAlert size={11} strokeWidth={1.75} /> Shared by a TenantTrail user
          </div>
          <p className="text-[13px] text-[#1A1A1A] font-serif leading-relaxed">
            This page shows Compass&apos;s analysis of a document that a renter chose to upload and share. The renter is solely responsible for the contents of this page, including the document analysed, what was included or redacted and the decision to share it publicly. TenantTrail provides the analytical tool. We do not author, endorse, verify or take responsibility for what is shared.
          </p>
          <p className="text-[12px] text-[#5a6b7a] font-serif italic">
            This is general information, not legal advice. The named parties may dispute the characterisation of any clause. For advice on a specific tenancy, contact your state&apos;s Tenants&apos; Union or a free tenant advice service.
          </p>
        </div>

        {/* Headline summary */}
        <div className="bg-white border border-[#1B6C62]/30 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ScanText size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
            <span className="font-medium text-[#0D2B52]">Compass&apos;s deep read</span>
            {flagCount > 0 && (
              <span className="font-mono text-[10px] tracking-wider uppercase text-[#B35D4C] bg-[#FAF6E8] border border-[#C9A961]/40 px-1.5 py-0.5">
                {flagCount} thing{flagCount === 1 ? "" : "s"} worth knowing
              </span>
            )}
            {data?.anonymise && (
              <span className="ml-auto font-mono text-[10px] tracking-wider uppercase text-[#1B6C62] bg-[#E6F5F2] border border-[#1B6C62]/30 px-1.5 py-0.5">
                <ShieldCheck size={10} strokeWidth={1.75} className="inline -mt-0.5 mr-0.5" /> Anonymised
              </span>
            )}
          </div>
          {a.overview && <p className="text-[14px] font-serif text-[#1A1A1A] leading-relaxed">{a.overview}</p>}
        </div>

        {/* Red flags */}
        {a.red_flags?.length > 0 && (
          <section className="bg-white border border-[#E5E4DE] p-5" data-testid="share-redflags">
            <div className="label-mono text-[10px] text-[#B35D4C] mb-2.5 flex items-center gap-1">
              <ShieldAlert size={11} strokeWidth={1.75} /> Red flags
            </div>
            <ul className="space-y-1.5 text-[13.5px] text-[#1A1A1A] font-serif">
              {a.red_flags.map((r, i) => (
                <li key={i} className="flex gap-2"><span className="text-[#B35D4C]">•</span><span>{r}</span></li>
              ))}
            </ul>
          </section>
        )}

        {/* Clauses to watch */}
        {a.clauses_to_watch?.length > 0 && (
          <section className="bg-white border border-[#E5E4DE] p-5" data-testid="share-clauses">
            <div className="label-mono text-[10px] text-[#8A6D3B] mb-2.5 flex items-center gap-1">
              <Quote size={11} strokeWidth={1.75} /> Clauses to watch
            </div>
            <ul className="space-y-3">
              {a.clauses_to_watch.map((c, i) => (
                <li key={i} className="bg-[#FAF6E8] border-l-2 border-[#C9A961] px-3 py-2.5">
                  {c.clause && <div className="font-mono text-[10px] text-[#8A6D3B] uppercase tracking-wider mb-1">{c.clause}</div>}
                  {c.verbatim_quote && <blockquote className="text-[13px] text-[#1A1A1A] italic font-serif mb-1.5">{`"${c.verbatim_quote}"`}</blockquote>}
                  {c.why_it_matters && <p className="text-[13px] text-[#0D2B52] font-serif leading-snug">{c.why_it_matters}</p>}
                  {c.act_section_potentially_breached && (
                    <p className="text-[11px] text-[#1B6C62] font-mono mt-1">Possibly conflicts with: {c.act_section_potentially_breached}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Unusual terms */}
        {a.unusual_terms?.length > 0 && (
          <section className="bg-white border border-[#E5E4DE] p-5" data-testid="share-unusual">
            <div className="label-mono text-[10px] text-[#8A6D3B] mb-2.5 flex items-center gap-1">
              <AlertTriangle size={11} strokeWidth={1.75} /> Unusual terms
            </div>
            <ul className="space-y-1 text-[13px] text-[#1A1A1A] font-serif list-disc list-inside marker:text-[#8A6D3B]">
              {a.unusual_terms.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </section>
        )}

        {/* Deadlines */}
        {a.deadlines_for_action?.length > 0 && (
          <section className="bg-white border border-[#E5E4DE] p-5" data-testid="share-deadlines">
            <div className="label-mono text-[10px] text-[#B35D4C] mb-2.5 flex items-center gap-1">
              <Calendar size={11} strokeWidth={1.75} /> Deadlines you can&apos;t miss
            </div>
            <ul className="space-y-1.5 text-[13px] text-[#1A1A1A] font-serif">
              {a.deadlines_for_action.map((d, i) => (
                <li key={i} className="border-l-2 border-[#B35D4C] pl-2.5">
                  <span className="font-mono text-[#0D2B52]">{d.by}</span>, {d.action}
                  {d.consequence && <div className="text-[11px] text-[#6B7280] italic">If missed: {d.consequence}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Key dates + money */}
        {(a.key_dates?.length > 0 || a.money_amounts?.length > 0) && (
          <section className="grid sm:grid-cols-2 gap-4">
            {a.key_dates?.length > 0 && (
              <div className="bg-white border border-[#E5E4DE] p-5" data-testid="share-dates">
                <div className="label-mono text-[10px] text-[#1B6C62] mb-2.5 flex items-center gap-1">
                  <Calendar size={11} strokeWidth={1.75} /> Key dates
                </div>
                <ul className="space-y-1 text-[12.5px] text-[#1A1A1A] font-serif">
                  {a.key_dates.map((k, i) => <li key={i}><span className="font-mono text-[#0D2B52]">{k.date}</span>, {k.what}</li>)}
                </ul>
              </div>
            )}
            {a.money_amounts?.length > 0 && (
              <div className="bg-white border border-[#E5E4DE] p-5" data-testid="share-money">
                <div className="label-mono text-[10px] text-[#1B6C62] mb-2.5 flex items-center gap-1">
                  <DollarSign size={11} strokeWidth={1.75} /> Money mentioned
                </div>
                <ul className="space-y-1 text-[12.5px] text-[#1A1A1A] font-serif">
                  {a.money_amounts.map((m, i) => <li key={i}><span className="font-mono text-[#0D2B52]">{m.amount}</span>, {m.what}</li>)}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* CTA: get your own */}
        <div className="bg-[#0D2B52] text-white p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="share-cta">
          <div>
            <p className="font-medium">Want Compass to read your lease too?</p>
            <p className="text-[13px] text-[#cdd6e2] font-serif mt-0.5">Free plan includes 3 deep reads. No credit card.</p>
          </div>
          <Link to="/register" className="bg-[#1B6C62] text-white px-4 py-2 text-sm font-medium hover:bg-[#175A52] whitespace-nowrap">Start free</Link>
        </div>

        {/* Related reading - drives traffic to the agent tricks landing page */}
        <div className="bg-[#FAF6E8] border-l-2 border-[#C9A961] p-4" data-testid="share-related">
          <div className="label-mono text-[10px] text-[#8A6D3B] mb-1">Related reading</div>
          <a href="/api/agent-tricks" className="text-[14px] text-[#0D2B52] font-serif hover:underline">12 rental clauses your agent hopes you don&apos;t read &rarr;</a>
        </div>

        {/* Footer + takedown report link */}
        <footer className="pt-4 border-t border-[#E5E4DE] text-[11px] text-[#9CA3AF] font-mono space-y-2">
          <p>Posted by the TenantTrail user who uploaded the original document. TenantTrail is a platform, not the publisher. Concerns about this page can be reported to support@tenanttrail.net.</p>
          <button onClick={() => setReportOpen(true)} className="inline-flex items-center gap-1 text-[#1B6C62] hover:underline uppercase tracking-wider" data-testid="share-report-button">
            <Flag size={10} strokeWidth={1.75} /> Report this page
          </button>
        </footer>
      </main>

      {reportOpen && <ReportModal token={token} onClose={() => setReportOpen(false)} />}
    </div>
  );
}


function ReportModal({ token, onClose }) {
  const [reason, setReason] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please describe why this should come down.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/findings-share/${token}/report`, {
        reason: reason.trim(),
        reporter_role: role || undefined,
        reporter_email: email || undefined,
      });
      setDone(true);
      if (data?.auto_revoked) {
        toast.success("Report received. This page has been taken down pending review.");
      } else {
        toast.success("Report received. We will review it.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit the report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 flex items-center justify-center p-4" onClick={onClose} data-testid="share-report-modal">
      <div className="bg-white max-w-md w-full border border-[#E5E4DE] shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E4DE]">
          <div className="flex items-center gap-2">
            <Flag size={14} strokeWidth={1.75} className="text-[#B35D4C]" />
            <span className="font-medium text-[#0D2B52]">Report this page</span>
          </div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#0D2B52]"><X size={16} strokeWidth={1.75} /></button>
        </div>
        {done ? (
          <div className="p-5 text-center space-y-3">
            <ShieldCheck size={22} className="text-[#1B6C62] mx-auto" strokeWidth={1.75} />
            <p className="text-sm text-[#1A1A1A] font-serif">Thanks. Your report has been received and will be reviewed.</p>
            <button onClick={onClose} className="btn-outline text-xs w-full">Close</button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <p className="text-[13px] text-[#1A1A1A] font-serif">
              Tell us why this page should be reviewed or taken down. We treat takedown reports seriously.
            </p>
            <div>
              <label className="label-mono text-[10px] text-[#6B7280] mb-1 block">You are (optional)</label>
              <select value={role} onChange={e => setRole(e.target.value)} className="input-bordered text-sm w-full" data-testid="share-report-role">
                <option value="">Select...</option>
                <option value="named party">A named party in this document</option>
                <option value="agent">The agent named</option>
                <option value="landlord">The landlord named</option>
                <option value="other">Other concerned party</option>
              </select>
            </div>
            <div>
              <label className="label-mono text-[10px] text-[#6B7280] mb-1 block">Your email (optional)</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="input-bordered text-sm w-full" data-testid="share-report-email" />
            </div>
            <div>
              <label className="label-mono text-[10px] text-[#6B7280] mb-1 block">Reason</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={4} placeholder="e.g. The document is misrepresented. The clause quoted does not appear in the original. Personal details I did not consent to were included." className="input-bordered text-sm w-full font-serif" data-testid="share-report-reason" />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E4DE]">
              <button onClick={onClose} className="text-xs text-[#6B7280] px-3 py-2" data-testid="share-report-cancel">Cancel</button>
              <button onClick={submit} disabled={busy || !reason.trim()} className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="share-report-submit">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Flag size={11} strokeWidth={1.75} />}
                Submit report
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
